/**
 * FormattingRuleEditorModal — Krok 7.2.3 kreatora widoków list.
 *
 * Modal „Nowa reguła" / „Edycja reguły" otwierany z menu kontekstowego
 * (po kliknięciu opcji w submenu Conditional Formatting) albo z managera
 * reguł (Krok 7.2.4).
 *
 * Sekcje:
 *  1. Typ reguły (disabled gdy preset z menu, edytowalny w trybie edycji)
 *  2. Warunek — dispatch komponent per typ (7 wariantów inline)
 *  3. Styl komórki (CellStylePicker) — ukryty dla typu all_cells
 *  4. Zakres aplikacji (komórka / wiersz)
 *  5. Preview
 *  6. Anuluj / Zapisz
 *
 * Po Save: zwraca FormattingRule do parenta przez `onSave(rule)`. Parent
 * (ConfigurableTable lub Manager Modal) dorzuca do `list_config.formattingRules`
 * i wywołuje saveLayout.
 */

import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';
import type {
  AboveBelowAvgCondition,
  AllCellsCondition,
  CellStyle,
  ChangingValuesCondition,
  FormattingRule,
  FormattingRuleType,
  FormulaCondition,
  HighlightCellsCondition,
  TopBottomCondition,
  UniqueDuplicateCondition,
} from '@saas/shared';
import { Star } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { Modal } from '@/components/Modal';

import { ColorPicker } from './ColorPicker';
import type { ColumnDefinition } from './types';

// ── Public API ───────────────────────────────────────────────────────────────

export interface FormattingRuleEditorModalProps<T = unknown> {
  open: boolean;
  /** Kolumna do której reguła jest przypisana (pre-set z menu kontekstowego). */
  columnId: string;
  columnLabel: string;
  /** Lista wszystkich kolumn — dla edytora `changing_values` (sortColumn) i `formula`. */
  availableColumns: ColumnDefinition<T>[];
  /** Tryb edycji — gdy podane, modal startuje z wartościami istniejącej reguły. */
  initialRule?: FormattingRule;
  /** Pre-set typu z menu (np. „Greater Than" → highlight_cells z operator=gt). */
  presetType?: FormattingRuleType;
  /** Pre-set warunku (np. operator=gt). Mergowany z domyślnymi wartościami. */
  presetCondition?: Record<string, unknown>;
  onSave: (rule: FormattingRule) => void;
  onClose: () => void;
}

// ── Domyślne wartości per typ reguły ─────────────────────────────────────────

const DEFAULT_STYLE: CellStyle = { backgroundColor: '#fef3c7' };

function defaultCondition(type: FormattingRuleType): FormattingRule['condition'] {
  switch (type) {
    case 'highlight_cells':
      return { operator: 'gt', value: 0 } as HighlightCellsCondition;
    case 'top_bottom':
      return { mode: 'top', unit: 'count', n: 10 } as TopBottomCondition;
    case 'above_below_avg':
      return { mode: 'above' } as AboveBelowAvgCondition;
    case 'unique_duplicate':
      return { mode: 'duplicate' } as UniqueDuplicateCondition;
    case 'changing_values':
      return { sortColumn: '' } as ChangingValuesCondition;
    case 'all_cells':
      return {
        subType: '2color',
        colorMin: '#22c55e',
        colorMax: '#ef4444',
      } as AllCellsCondition;
    case 'formula':
      return { expression: '' } as FormulaCondition;
  }
}

// ── Komponent ────────────────────────────────────────────────────────────────

export function FormattingRuleEditorModal<T>({
  open,
  columnId,
  columnLabel,
  availableColumns,
  initialRule,
  presetType,
  presetCondition,
  onSave,
  onClose,
}: FormattingRuleEditorModalProps<T>): React.ReactElement | null {
  const [type, setType] = useState<FormattingRuleType>(
    initialRule?.type ?? presetType ?? 'highlight_cells',
  );
  const [condition, setCondition] = useState<FormattingRule['condition']>(() => {
    if (initialRule) return initialRule.condition;
    const base = defaultCondition(presetType ?? 'highlight_cells');
    return { ...base, ...(presetCondition ?? {}) } as FormattingRule['condition'];
  });
  const [style, setStyle] = useState<CellStyle>(
    initialRule && 'style' in initialRule ? initialRule.style : DEFAULT_STYLE,
  );
  const [applyTo, setApplyTo] = useState<'cell' | 'row'>(initialRule?.applyTo ?? 'cell');
  const [name, setName] = useState<string>(initialRule?.name ?? '');

  // Preview style — komórka z aplikowanym stylem.
  // (useMemo PRZED early return żeby zachować stabilną kolejność hooków.)
  const previewStyle = useMemo<CSSProperties>(() => {
    if (type === 'all_cells') {
      const cond = condition as AllCellsCondition;
      if (cond.subType === '2color')
        return {
          background: `linear-gradient(to right, ${cond.colorMin}, ${cond.colorMax})`,
        };
      if (cond.subType === '3color')
        return {
          background: `linear-gradient(to right, ${cond.colorMin}, ${cond.colorMid}, ${cond.colorMax})`,
        };
      if (cond.subType === 'data_bar')
        return {
          background: `linear-gradient(to right, ${cond.color} 60%, transparent 60%)`,
        };
      return {};
    }
    return {
      backgroundColor: style.backgroundColor,
      color: style.color,
      borderColor: style.borderColor,
      borderWidth: style.borderWidth ? `${style.borderWidth}px` : undefined,
      borderStyle: style.borderColor ? 'solid' : undefined,
      fontWeight: style.fontWeight,
      fontStyle: style.fontStyle,
    };
  }, [type, condition, style]);

  // Gdy zmieniamy typ — reset condition do defaultów tego typu.
  function handleTypeChange(next: FormattingRuleType) {
    setType(next);
    setCondition(defaultCondition(next));
  }

  function handleSave() {
    const id = initialRule?.id ?? crypto.randomUUID();
    const baseFields = {
      id,
      columnId,
      applyTo,
      priority: initialRule?.priority ?? 0,
      enabled: initialRule?.enabled ?? true,
      ...(name ? { name } : {}),
    };
    let rule: FormattingRule;
    if (type === 'all_cells') {
      rule = { ...baseFields, type, condition: condition as AllCellsCondition } as FormattingRule;
    } else {
      rule = {
        ...baseFields,
        type,
        condition,
        style,
      } as FormattingRule;
    }
    onSave(rule);
  }

  return (
    // Małe okno (kilka kontrolek) — bez pytania o porzucenie zmian; leży nad managerem reguł
    // (kolejność na stosie okien, bez z-index), a paleta `ColorPicker` nad nim.
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={initialRule ? 'Edytuj regułę formatowania' : 'Nowa reguła formatowania'}
      description={`Kolumna: ${columnLabel}`}
      testId="fmt-rule-editor"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={handleSave}>Zapisz regułę</Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Nazwa */}
        <Section title="Nazwa (opcjonalna)">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`np. „Faktura overdue"`}
            className="w-full rounded-md border border-border-secondary bg-bg-primary px-2 py-1 text-sm"
            maxLength={100}
          />
        </Section>

        {/* Typ reguły */}
        <Section title="Typ reguły">
          <TypeSelector value={type} onChange={handleTypeChange} />
        </Section>

        {/* Warunek per typ */}
        <Section title="Warunek">
          <ConditionEditor
            type={type}
            condition={condition}
            setCondition={setCondition}
            availableColumns={availableColumns}
            currentColumnId={columnId}
          />
        </Section>

        {/* Styl (ukryty dla all_cells) */}
        {type !== 'all_cells' && (
          <Section title="Styl komórki">
            <CellStylePicker style={style} setStyle={setStyle} />
          </Section>
        )}

        {/* Zakres aplikacji */}
        <Section title="Zakres aplikacji">
          <div className="flex gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                checked={applyTo === 'cell'}
                onChange={() => setApplyTo('cell')}
              />
              Tylko ta komórka
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="radio" checked={applyTo === 'row'} onChange={() => setApplyTo('row')} />
              Cały wiersz
            </label>
          </div>
        </Section>

        {/* Preview */}
        <Section title="Podgląd">
          <div className="rounded border border-dashed border-gray-300 p-3 dark:border-gray-600">
            <div
              className="inline-block min-w-[120px] rounded px-3 py-1 text-sm"
              style={previewStyle}
            >
              Przykładowa wartość
            </div>
          </div>
        </Section>
      </div>
    </Modal>
  );
}

// ── Sub-komponenty ───────────────────────────────────────────────────────────

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
        {title}
      </div>
      {children}
    </div>
  );
}

const TYPE_OPTIONS: { value: FormattingRuleType; label: string; description: string }[] = [
  {
    value: 'highlight_cells',
    label: 'Komórki spełniające warunek',
    description: 'Operator + wartość (>, <, =, zawiera, między...)',
  },
  {
    value: 'top_bottom',
    label: 'Górne / dolne wartości',
    description: 'Top/bottom N lub procent',
  },
  {
    value: 'above_below_avg',
    label: 'Powyżej / poniżej średniej',
    description: 'Średnia liczona dynamicznie',
  },
  {
    value: 'unique_duplicate',
    label: 'Unikalne lub powtarzające się',
    description: 'Wartość występuje raz lub więcej razy',
  },
  {
    value: 'changing_values',
    label: 'Zmieniające się wartości',
    description: 'Wartość różna od poprzedniego wiersza',
  },
  {
    value: 'all_cells',
    label: 'Wszystkie komórki (gradient)',
    description: '2-Color / 3-Color Scale, pasek danych, ikony',
  },
  {
    value: 'formula',
    label: 'Formuła',
    description: 'Wyrażenie z @kolumna i operatorami (>, AND, OR...)',
  },
];

function TypeSelector({
  value,
  onChange,
}: {
  value: FormattingRuleType;
  onChange: (next: FormattingRuleType) => void;
}): React.ReactElement {
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
      {TYPE_OPTIONS.map((opt) => (
        <label
          key={opt.value}
          className={`flex cursor-pointer items-start gap-2 rounded-md border px-2 py-1.5 text-sm ${
            value === opt.value
              ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
              : 'border-border-secondary hover:bg-bg-secondary'
          }`}
        >
          <input
            type="radio"
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="mt-0.5"
          />
          <div>
            <div className="font-medium text-text-primary">{opt.label}</div>
            <div className="text-xs text-text-tertiary">{opt.description}</div>
          </div>
        </label>
      ))}
    </div>
  );
}

// ── Condition editors per typ ───────────────────────────────────────────────

interface ConditionEditorProps<T> {
  type: FormattingRuleType;
  condition: FormattingRule['condition'];
  setCondition: (next: FormattingRule['condition']) => void;
  availableColumns: ColumnDefinition<T>[];
  currentColumnId: string;
}

function ConditionEditor<T>({
  type,
  condition,
  setCondition,
  availableColumns,
  currentColumnId,
}: ConditionEditorProps<T>): React.ReactElement {
  switch (type) {
    case 'highlight_cells':
      return (
        <HighlightCellsEditor
          condition={condition as HighlightCellsCondition}
          setCondition={(c) => setCondition(c)}
        />
      );
    case 'top_bottom':
      return (
        <TopBottomEditor
          condition={condition as TopBottomCondition}
          setCondition={(c) => setCondition(c)}
        />
      );
    case 'above_below_avg':
      return (
        <AboveBelowAvgEditor
          condition={condition as AboveBelowAvgCondition}
          setCondition={(c) => setCondition(c)}
        />
      );
    case 'unique_duplicate':
      return (
        <UniqueDuplicateEditor
          condition={condition as UniqueDuplicateCondition}
          setCondition={(c) => setCondition(c)}
        />
      );
    case 'changing_values':
      return (
        <ChangingValuesEditor
          condition={condition as ChangingValuesCondition}
          setCondition={(c) => setCondition(c)}
          availableColumns={availableColumns}
          currentColumnId={currentColumnId}
        />
      );
    case 'all_cells':
      return (
        <AllCellsEditor
          condition={condition as AllCellsCondition}
          setCondition={(c) => setCondition(c)}
        />
      );
    case 'formula':
      return (
        <FormulaEditor
          condition={condition as FormulaCondition}
          setCondition={(c) => setCondition(c)}
        />
      );
  }
}

const OPERATOR_LABELS: Record<HighlightCellsCondition['operator'], string> = {
  gt: 'większe niż (>)',
  gte: 'większe lub równe (≥)',
  lt: 'mniejsze niż (<)',
  lte: 'mniejsze lub równe (≤)',
  eq: 'równe (=)',
  neq: 'różne od (≠)',
  contains: 'zawiera tekst',
  not_contains: 'nie zawiera tekstu',
  starts_with: 'zaczyna się od',
  ends_with: 'kończy się na',
  between: 'pomiędzy',
  not_between: 'nie pomiędzy',
  is_null: 'jest puste',
  is_not_null: 'nie jest puste',
};

function HighlightCellsEditor({
  condition,
  setCondition,
}: {
  condition: HighlightCellsCondition;
  setCondition: (c: HighlightCellsCondition) => void;
}): React.ReactElement {
  const showValue = condition.operator !== 'is_null' && condition.operator !== 'is_not_null';
  const showValue2 = condition.operator === 'between' || condition.operator === 'not_between';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Dropdown
        options={Object.entries(OPERATOR_LABELS).map(([op, label]) => ({ value: op, label }))}
        value={condition.operator}
        onChange={(val) =>
          setCondition({
            ...condition,
            operator: val as HighlightCellsCondition['operator'],
          })
        }
        aria-label="Operator"
        className="w-auto px-2 py-1"
      />
      {showValue && (
        <input
          type="text"
          value={condition.value == null ? '' : String(condition.value)}
          onChange={(e) => {
            const v = e.target.value;
            const num = parseFloat(v);
            setCondition({ ...condition, value: !isNaN(num) && String(num) === v ? num : v });
          }}
          placeholder="Wartość"
          className="w-32 rounded-md border border-border-secondary bg-bg-primary px-2 py-1 text-sm"
        />
      )}
      {showValue2 && (
        <>
          <span className="text-sm text-text-tertiary">i</span>
          <input
            type="text"
            value={condition.value2 == null ? '' : String(condition.value2)}
            onChange={(e) => {
              const v = e.target.value;
              const num = parseFloat(v);
              setCondition({ ...condition, value2: !isNaN(num) && String(num) === v ? num : v });
            }}
            placeholder="Górny limit"
            className="w-32 rounded-md border border-border-secondary bg-bg-primary px-2 py-1 text-sm"
          />
        </>
      )}
    </div>
  );
}

function TopBottomEditor({
  condition,
  setCondition,
}: {
  condition: TopBottomCondition;
  setCondition: (c: TopBottomCondition) => void;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Dropdown
        options={[
          { value: 'top', label: 'Górne (top)' },
          { value: 'bottom', label: 'Dolne (bottom)' },
        ]}
        value={condition.mode}
        onChange={(val) => setCondition({ ...condition, mode: val as TopBottomCondition['mode'] })}
        aria-label="Tryb"
        className="w-auto px-2 py-1"
      />
      <input
        type="number"
        min={1}
        value={condition.n}
        onChange={(e) => setCondition({ ...condition, n: parseInt(e.target.value) || 1 })}
        className="w-20 rounded-md border border-border-secondary bg-bg-primary px-2 py-1"
      />
      <Dropdown
        options={[
          { value: 'count', label: 'rekordów' },
          { value: 'percent', label: '%' },
        ]}
        value={condition.unit}
        onChange={(val) => setCondition({ ...condition, unit: val as TopBottomCondition['unit'] })}
        aria-label="Jednostka"
        className="w-auto px-2 py-1"
      />
    </div>
  );
}

function AboveBelowAvgEditor({
  condition,
  setCondition,
}: {
  condition: AboveBelowAvgCondition;
  setCondition: (c: AboveBelowAvgCondition) => void;
}): React.ReactElement {
  return (
    <Dropdown
      options={[
        { value: 'above', label: 'Powyżej średniej' },
        { value: 'below', label: 'Poniżej średniej' },
        { value: 'above_or_equal', label: 'Powyżej lub równe średniej' },
        { value: 'below_or_equal', label: 'Poniżej lub równe średniej' },
      ]}
      value={condition.mode}
      onChange={(val) => setCondition({ mode: val as AboveBelowAvgCondition['mode'] })}
      aria-label="Tryb"
      className="w-auto px-2 py-1"
    />
  );
}

function UniqueDuplicateEditor({
  condition,
  setCondition,
}: {
  condition: UniqueDuplicateCondition;
  setCondition: (c: UniqueDuplicateCondition) => void;
}): React.ReactElement {
  return (
    <Dropdown
      options={[
        { value: 'unique', label: 'Tylko unikalne (1 wystąpienie)' },
        { value: 'duplicate', label: 'Tylko powtarzające się (≥2)' },
      ]}
      value={condition.mode}
      onChange={(val) => setCondition({ mode: val as UniqueDuplicateCondition['mode'] })}
      aria-label="Tryb"
      className="w-auto px-2 py-1"
    />
  );
}

function ChangingValuesEditor<T>({
  condition,
  setCondition,
  availableColumns,
  currentColumnId,
}: {
  condition: ChangingValuesCondition;
  setCondition: (c: ChangingValuesCondition) => void;
  availableColumns: ColumnDefinition<T>[];
  currentColumnId: string;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-text-tertiary">Sortuj po kolumnie:</span>
      <Dropdown
        options={availableColumns.map((c) => ({
          value: c.id,
          label: typeof c.header === 'string' ? c.header : c.id,
        }))}
        value={condition.sortColumn || currentColumnId}
        onChange={(val) => setCondition({ sortColumn: val })}
        aria-label="Sortuj po kolumnie"
        className="w-auto px-2 py-1"
      />
    </div>
  );
}

function AllCellsEditor({
  condition,
  setCondition,
}: {
  condition: AllCellsCondition;
  setCondition: (c: AllCellsCondition) => void;
}): React.ReactElement {
  return (
    <div className="space-y-2">
      <Dropdown
        options={[
          { value: '2color', label: 'Gradient 2-kolorowy' },
          { value: '3color', label: 'Gradient 3-kolorowy' },
          { value: 'data_bar', label: 'Pasek danych' },
          { value: 'icon_set', label: 'Zestaw ikon' },
        ]}
        value={condition.subType}
        onChange={(val) => {
          const next = val as AllCellsCondition['subType'];
          if (next === '2color')
            setCondition({ subType: '2color', colorMin: '#22c55e', colorMax: '#ef4444' });
          else if (next === '3color')
            setCondition({
              subType: '3color',
              colorMin: '#22c55e',
              colorMid: '#fef3c7',
              colorMax: '#ef4444',
            });
          else if (next === 'data_bar')
            setCondition({ subType: 'data_bar', color: '#3b82f6', showValue: true });
          else setCondition({ subType: 'icon_set', setName: 'arrows_3' });
        }}
        aria-label="Wariant"
        className="w-auto px-2 py-1"
      />
      {condition.subType === '2color' && (
        <div className="flex items-center gap-2 text-sm">
          <span>Min:</span>
          <ColorPicker
            value={condition.colorMin}
            onChange={(v) => v && setCondition({ ...condition, colorMin: v })}
            allowClear={false}
          />
          <span>Max:</span>
          <ColorPicker
            value={condition.colorMax}
            onChange={(v) => v && setCondition({ ...condition, colorMax: v })}
            allowClear={false}
          />
        </div>
      )}
      {condition.subType === '3color' && (
        <div className="flex items-center gap-2 text-sm">
          <span>Min:</span>
          <ColorPicker
            value={condition.colorMin}
            onChange={(v) => v && setCondition({ ...condition, colorMin: v })}
            allowClear={false}
          />
          <span>Mid:</span>
          <ColorPicker
            value={condition.colorMid}
            onChange={(v) => v && setCondition({ ...condition, colorMid: v })}
            allowClear={false}
          />
          <span>Max:</span>
          <ColorPicker
            value={condition.colorMax}
            onChange={(v) => v && setCondition({ ...condition, colorMax: v })}
            allowClear={false}
          />
        </div>
      )}
      {condition.subType === 'data_bar' && (
        <div className="flex items-center gap-2 text-sm">
          <span>Kolor paska:</span>
          <ColorPicker
            value={condition.color}
            onChange={(v) => v && setCondition({ ...condition, color: v })}
            allowClear={false}
          />
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={condition.showValue ?? true}
              onChange={(e) => setCondition({ ...condition, showValue: e.target.checked })}
            />
            Pokaż wartość
          </label>
        </div>
      )}
      {condition.subType === 'icon_set' && (
        <div className="flex items-center gap-2 text-sm">
          <span>Zestaw:</span>
          <Dropdown
            options={[
              { value: 'arrows_3', label: 'Strzałki (3)' },
              { value: 'arrows_5', label: 'Strzałki (5)' },
              { value: 'traffic_3', label: 'Sygnalizacja (3)' },
              { value: 'stars_5', label: 'Gwiazdki (5)' },
            ]}
            value={condition.setName}
            onChange={(val) =>
              setCondition({
                ...condition,
                setName: val as AllCellsCondition['setName' & keyof AllCellsCondition],
              } as AllCellsCondition)
            }
            aria-label="Zestaw ikon"
            className="w-auto px-2 py-1"
          />
        </div>
      )}
    </div>
  );
}

function FormulaEditor({
  condition,
  setCondition,
}: {
  condition: FormulaCondition;
  setCondition: (c: FormulaCondition) => void;
}): React.ReactElement {
  return (
    <div className="space-y-1">
      <textarea
        value={condition.expression}
        onChange={(e) => setCondition({ expression: e.target.value })}
        placeholder="np. @status = 'blocked' AND @amount > 1000"
        rows={2}
        maxLength={500}
        className="w-full rounded-md border border-border-secondary bg-bg-primary px-2 py-1 font-mono text-sm"
      />
      <p className="text-xs text-text-tertiary">
        Składnia: <code>@kolumna</code> + operatory <code>&gt; &lt; = != &gt;= &lt;=</code>,
        kombinatory <code>AND OR NOT</code>, nawiasy <code>( )</code>. Stringi w cudzysłowiu
        pojedynczym <code>&apos;...&apos;</code>.
      </p>
    </div>
  );
}

// ── CellStylePicker (color × 3 + toggle bold/italic + border width) ─────────

function CellStylePicker({
  style,
  setStyle,
}: {
  style: CellStyle;
  setStyle: (s: CellStyle) => void;
}): React.ReactElement {
  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span>Tło:</span>
          <ColorPicker
            value={style.backgroundColor}
            onChange={(v) => setStyle({ ...style, backgroundColor: v })}
          />
        </div>
        <div className="flex items-center gap-2">
          <span>Tekst:</span>
          <ColorPicker value={style.color} onChange={(v) => setStyle({ ...style, color: v })} />
        </div>
        <div className="flex items-center gap-2">
          <span>Obramowanie:</span>
          <ColorPicker
            value={style.borderColor}
            onChange={(v) => setStyle({ ...style, borderColor: v })}
          />
          {style.borderColor && (
            <input
              type="range"
              min={1}
              max={5}
              value={style.borderWidth ?? 1}
              onChange={(e) => setStyle({ ...style, borderWidth: parseInt(e.target.value) })}
              className="w-20"
              title={`Grubość: ${style.borderWidth ?? 1}px`}
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() =>
            setStyle({
              ...style,
              fontWeight: style.fontWeight === 'bold' ? 'normal' : 'bold',
            })
          }
          className={`rounded-md border px-2 py-0.5 font-bold ${
            style.fontWeight === 'bold'
              ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
              : 'border-border-secondary'
          }`}
          aria-pressed={style.fontWeight === 'bold'}
        >
          B
        </button>
        <button
          type="button"
          onClick={() =>
            setStyle({
              ...style,
              fontStyle: style.fontStyle === 'italic' ? 'normal' : 'italic',
            })
          }
          className={`rounded-md border px-2 py-0.5 italic ${
            style.fontStyle === 'italic'
              ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
              : 'border-border-secondary'
          }`}
          aria-pressed={style.fontStyle === 'italic'}
        >
          I
        </button>
        <Star size={14} className="ml-2 text-text-tertiary" aria-hidden="true" />
        <span className="text-xs text-text-tertiary">Pozostałe efekty — pogrubienie i kursywa</span>
      </div>
    </div>
  );
}
